import { useEffect, useRef, useState } from "react";
import { CoverImg } from "@/components/ui/CoverImg";
import { IconNext, IconPause, IconPlay, IconPrev, IconVolume } from "@/components/ui/icons";
import { Marquee } from "@/components/ui/Marquee";
import { Slider } from "@/components/ui/Slider";
import { useConnect, type ConnectDevice } from "@/contexts/ConnectContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { sfx } from "@/lib/sfx";
import { fmtDuration } from "@/utils/format";
import { DevicesButton } from "./DevicesButton";

/** Playbar shown while another of your devices is the one playing: the
 *  controls drive that device (slowson Connect). */
export function RemotePlaybar({ device }: { device: ConnectDevice }) {
  const connect = useConnect()!;
  const isMobile = useIsMobile(800);
  const st = device.state!;
  const [, tick] = useState(0);
  const [vol, setVol] = useState(st.volume);
  const volTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const t = window.setInterval(() => tick((x) => x + 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => { setVol(st.volume); }, [st.volume]);

  const dur = st.duration || (st.track?.duration_ms ?? 0) / 1000;
  const pos = Math.min(dur || Infinity, st.position + (st.playing ? (Date.now() - st.ts) / 1000 : 0));
  const cmd = (c: Parameters<typeof connect.sendCmd>[1], args?: Record<string, unknown>) =>
    connect.sendCmd(device.id, c, args);

  const onVolume = (v: number) => {
    setVol(v);
    window.clearTimeout(volTimer.current);
    volTimer.current = window.setTimeout(() => cmd("volume", { v }), 150);
  };

  const t = st.track;
  return (
    <div className={"remote-bar" + (isMobile ? " mobile" : "")}>
      <div className="remote-top">
        <span className="remote-live">🔊 Lecture sur <b>{device.name}</b></span>
        <button className="remote-here" onClick={() => { sfx.pop(); connect.playOn(connect.selfId); }}>
          Écouter ici
        </button>
      </div>
      <div className="remote-main">
        <div className="row" style={{ gap: 10, minWidth: 0 }}>
          <CoverImg coverId={t?.album?.cover_id} alt={t?.title ?? ""} size={40} />
          <div style={{ minWidth: 0 }}>
            <Marquee style={{ fontWeight: 700 }}>{t?.title ?? ""}</Marquee>
            <div className="muted trunc" style={{ fontSize: 13 }}>{t?.artist?.name ?? ""}</div>
          </div>
        </div>
        <div className="row" style={{ gap: 14, justifyContent: "center" }}>
          <button title="Précédent" onClick={() => { sfx.tap(); cmd("prev"); }}><IconPrev /></button>
          <button className="btn primary" style={{ padding: 10, borderRadius: 999 }}
                  title={st.playing ? "Pause" : "Lecture"}
                  onClick={() => { sfx.pop(); cmd("toggle"); }}>
            {st.playing ? <IconPause /> : <IconPlay />}
          </button>
          <button title="Suivant" onClick={() => { sfx.tap(); cmd("next"); }}><IconNext /></button>
        </div>
        {!isMobile && (
          <div className="row" style={{ gap: 10, justifyContent: "flex-end" }}>
            <DevicesButton />
            <IconVolume />
            <Slider value={vol} max={1} step={0.01} onChange={onVolume} width={110} ariaLabel="volume de l'autre appareil" />
          </div>
        )}
        {isMobile && <DevicesButton />}
      </div>
      <div className="remote-progress">
        <span>{fmtDuration(pos * 1000)}</span>
        <Slider value={dur ? pos : 0} max={dur || 1} step={1} disabled={!dur}
                onChange={(v) => cmd("seek", { sec: v })} ariaLabel="position sur l'autre appareil" />
        <span>{fmtDuration(dur * 1000)}</span>
      </div>
    </div>
  );
}
